/**
 * T155P4 — RulePreviewModal
 *
 * Dialog showing before/after rows from the dryRun payload.
 * User must explicitly click "Apply" to commit — no silent auto-apply.
 *
 * data-testid="rule-preview-modal"
 * data-testid="rule-preview-confirm"
 */
import { Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAppSelector } from '@/features/hooks';
import { useCurrency } from '@/hooks/useCurrency';
import type { RulePreviewRow } from '@/lib/types';

interface RulePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function FieldDiff({
  label,
  before,
  after,
}: {
  label: string;
  before: string | null;
  after: string | null;
}) {
  if (before === after) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs flex-wrap">
      <span className="text-muted-foreground">{label}:</span>
      <span className="line-through text-muted-foreground">{before ?? '—'}</span>
      <ArrowRight className="size-3 text-muted-foreground shrink-0" />
      <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300 border-green-200 dark:border-green-800">
        {after ?? '—'}
      </Badge>
    </div>
  );
}

function PreviewRowDesktop({ row }: { row: RulePreviewRow }) {
  const { fmt } = useCurrency();

  const categoryChanged = row.before.category !== row.after.category;
  const typeChanged = row.before.type !== row.after.type;
  const tagsChanged = JSON.stringify(row.before.tags) !== JSON.stringify(row.after.tags);

  return (
    <TableRow>
      <TableCell className="py-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-sm line-clamp-1">{row.description}</span>
          {row.merchant && (
            <span className="text-xs text-muted-foreground">{row.merchant}</span>
          )}
          <span className="text-xs text-muted-foreground">
            {new Date(row.date).toLocaleDateString()}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-2 tabular-nums text-sm font-medium text-right whitespace-nowrap">
        {fmt(Math.abs(row.amount))}
      </TableCell>
      <TableCell className="py-2">
        <div className="flex flex-col gap-1">
          {categoryChanged && (
            <FieldDiff label="Category" before={row.before.category} after={row.after.category} />
          )}
          {typeChanged && (
            <FieldDiff label="Type" before={row.before.type} after={row.after.type} />
          )}
          {tagsChanged && (
            <FieldDiff
              label="Tags"
              before={row.before.tags.join(', ') || null}
              after={row.after.tags.join(', ') || null}
            />
          )}
        </div>
      </TableCell>
      <TableCell className="py-2">
        <span className="text-xs text-muted-foreground">{row.reason}</span>
      </TableCell>
    </TableRow>
  );
}

export function RulePreviewModal({ open, onOpenChange, onConfirm }: RulePreviewModalProps) {
  const dryRunResult = useAppSelector((s) => s.transactionRules.dryRunResult);
  const isApplying = useAppSelector((s) => s.transactionRules.isApplying);
  const { fmt } = useCurrency();

  if (!dryRunResult) return null;

  const { preview, appliedCount, hasMorePreview } = dryRunResult;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-full max-h-[90dvh] flex flex-col gap-0 p-0"
        data-testid="rule-preview-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            Preview Rule Changes
            <Badge variant="secondary" className="ml-1">
              {appliedCount}{hasMorePreview ? '+' : ''} transaction{appliedCount !== 1 ? 's' : ''}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Review the changes your automation rules will make.
            {hasMorePreview && ` Showing a sample of ${preview.length} — all ${appliedCount}+ will be applied.`}
            {' '}No changes have been made yet.
          </DialogDescription>
        </DialogHeader>

        {/* Desktop table */}
        <div className="flex-1 overflow-y-auto px-6 hidden md:block min-h-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Changes</TableHead>
                <TableHead>Rule</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.map((row) => (
                <PreviewRowDesktop key={row.transactionId} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile list */}
        <div className="flex-1 overflow-y-auto px-4 md:hidden min-h-0 space-y-3 py-2">
          {preview.map((row) => {
            const categoryChanged = row.before.category !== row.after.category;
            const typeChanged = row.before.type !== row.after.type;
            return (
              <div key={row.transactionId} className="rounded-lg border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm break-words">{row.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(row.date).toLocaleDateString()}
                      {row.merchant ? ` · ${row.merchant}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-medium tabular-nums shrink-0">{fmt(Math.abs(row.amount))}</span>
                </div>
                <div className="space-y-1">
                  {categoryChanged && (
                    <FieldDiff label="Category" before={row.before.category} after={row.after.category} />
                  )}
                  {typeChanged && (
                    <FieldDiff label="Type" before={row.before.type} after={row.after.type} />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{row.reason}</p>
              </div>
            );
          })}
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
            className="min-h-[40px]"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isApplying}
            className="min-h-[40px]"
            data-testid="rule-preview-confirm"
          >
            {isApplying ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Applying…
              </>
            ) : (
              <>
                <CheckCircle2 className="size-4 mr-2" />
                Apply {appliedCount}{hasMorePreview ? '+' : ''} Change{appliedCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
